/**
 * Tracking cookies utility
 *
 * Manages first-party cookies that bridge tracking identifiers across
 * the Astro marketing site and React SPA. Both apps share the same domain
 * (jobtrackr.it), so cookies are accessible everywhere.
 *
 * Stored in `jt_tracking` cookie as JSON:
 *   { fbp, fbc, li_fat_id, event_source_url }
 */

export interface TrackingData {
  fbp?: string        // Meta _fbp browser ID
  fbc?: string        // Meta _fbc click ID
  li_fat_id?: string  // LinkedIn first-party cookie
  event_source_url?: string
}

const COOKIE_NAME = 'jt_tracking'
const MAX_AGE = 90 * 24 * 60 * 60 // 90 days

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function setCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`
}

/** Read the jt_tracking cookie */
export function getTrackingData(): TrackingData {
  const raw = getCookie(COOKIE_NAME)
  if (!raw) return {}
  try {
    return JSON.parse(raw) as TrackingData
  } catch {
    return {}
  }
}

/** Write updated tracking data to the jt_tracking cookie */
export function setTrackingData(data: TrackingData): void {
  const existing = getTrackingData()
  const merged = { ...existing, ...data }
  setCookie(COOKIE_NAME, JSON.stringify(merged), MAX_AGE)
}

/**
 * Sync native platform cookies into jt_tracking.
 * Call this on page load (both Astro and React) to capture
 * _fbp, _fbc, and li_fat_id set by Meta/LinkedIn pixels.
 */
export function syncTrackingCookies(): void {
  if (typeof document === 'undefined') return

  const updates: TrackingData = {}

  const fbp = getCookie('_fbp')
  if (fbp) updates.fbp = fbp

  const fbc = getCookie('_fbc')
  if (fbc) updates.fbc = fbc

  const liFatId = getCookie('li_fat_id')
  if (liFatId) updates.li_fat_id = liFatId

  updates.event_source_url = window.location.href

  if (Object.keys(updates).length > 0) {
    setTrackingData(updates)
  }
}

/**
 * Generate a unique event ID for deduplication between
 * client-side pixel events and server-side CAPI events.
 */
export function generateEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}
